import { useState } from 'react'
import { type Column } from '@/components/organisms'
import { OrderbookTableContentBaseProps, formatDateDDMMYYYY } from './OrderbookTableContentBase'
import type { OrderbookItem } from '@/types/otc'
import { formatCompactNumber, getOrderTypeVariant } from '@/utils'
import { Badge } from '@/components/ui/badge'
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll'
import { ChevronsUpDown, Search, SlidersHorizontal } from 'lucide-react'
import { cn } from '@/utils'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
  DrawerClose,
} from '@/components/ui/drawer'
import { OTCFilters } from '@/components/filters'

// Get columns definition for mobile table (horizontal scrollable)
const getMobileOrderbookColumns = (): Column<OrderbookItem>[] => [
  {
    key: 'project_name',
    title: 'Project',
    width: 'w-[235px]',
    render: (item: OrderbookItem) => (
      <div className="flex items-center gap-[10px]">
        <div className="relative shrink-0">
          {item.logo ? (
            <img
              src={item.logo}
              alt={item.project_name}
              className="h-[26px] w-[26px] rounded-full object-cover"
              onError={(e) => {
                e.currentTarget.src = '/placeholder-logo.png'
              }}
            />
          ) : (
            <div className="h-[26px] w-[26px] rounded-full bg-[#f5f5f5] flex items-center justify-center">
              <span className="text-xs font-semibold text-muted-foreground">
                {item.project_name?.charAt(0).toUpperCase() || '?'}
              </span>
            </div>
          )}
        </div>
        <span className="font-plus-jakarta font-medium text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] overflow-hidden text-ellipsis whitespace-nowrap">
          {item.project_name}
        </span>
      </div>
    ),
    sortable: false
  },
  {
    key: 'type',
    title: 'Type',
    width: 'w-[170px]',
    render: (item: OrderbookItem) => (
      <Badge variant={getOrderTypeVariant(item.type)}>
        {item.type}
      </Badge>
    ),
    sortable: true
  },
  {
    key: 'deal_type',
    title: 'Asset Type',
    width: 'w-[180px]',
    render: (item: OrderbookItem) => (
      <div className="bg-[#3481ec] flex items-center justify-center h-full px-[24px] py-[2px]">
        <span className="font-plus-jakarta font-semibold text-[12px] leading-[16px] text-white">
          {item.deal_type}
        </span>
      </div>
    ),
    sortable: true
  },
  {
    key: 'offered_amount',
    title: 'Amount',
    width: 'w-[230px]',
    render: (item: OrderbookItem) => (
      <span className="font-plus-jakarta font-normal text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
        ${formatCompactNumber(item.offered_amount)}
      </span>
    ),
    sortable: true
  },
  {
    key: 'offered_fully_diluted_value',
    title: 'FDV',
    width: 'w-[230px]',
    render: (item: OrderbookItem) => (
      <span className="font-plus-jakarta font-normal text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
        ${formatCompactNumber(item.offered_fully_diluted_value)}
      </span>
    ),
    sortable: true
  },
  {
    key: 'round',
    title: 'Round',
    width: 'w-[200px]',
    render: (item: OrderbookItem) => (
      <span className="font-plus-jakarta font-normal text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
        {item.round || '－'}
      </span>
    ),
    sortable: true
  },
  {
    key: 'date',
    title: 'Data',
    width: 'w-[232px]',
    render: (item: OrderbookItem) => (
      <span className="font-plus-jakarta font-normal text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
        {formatDateDDMMYYYY(item.date)}
      </span>
    ),
    sortable: true
  }
]

export const OrderbookTableContentMobile = ({
  data,
  isLoading = false,
  onRowClick,
  emptyMessage = 'No orders available',
  onSort,
  search = '',
  onSearchChange,
  type = '',
  onTypeChange,
  assetType = '',
  onAssetTypeChange,
  trancheSize = '',
  onTrancheSizeChange,
  sortBy = '',
  onSortByChange,
  sortDirectionFilter = '',
  onSortDirectionChange,
  onFilterButtonClick
}: OrderbookTableContentBaseProps) => {
  const { scrollContainerRef, handlers, style } = useHorizontalScroll()
  const columns = getMobileOrderbookColumns()
  const [filtersOpen, setFiltersOpen] = useState(false)

  const handleFilterClick = () => {
    if (onFilterButtonClick) {
      onFilterButtonClick()
    } else {
      setFiltersOpen(true)
    }
  }

  return (
    <div className="flex flex-col">
      {/* Search and Filter - Figma node 344-4482 */}
      <div className="flex items-center gap-[8px] mb-4">
        {/* Search Input */}
        <div className="flex-1 flex items-center gap-[8px] bg-[rgba(255,255,255,0.44)] dark:bg-[rgba(253,253,253,0.08)] px-[12px] py-[18px]">
          <Search size={16} className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)] shrink-0" />
          <input
            type="text"
            placeholder="Search by project name"
            value={search || ''}
            onChange={(e) => onSearchChange?.(e.target.value)}
            className="flex-1 bg-transparent border-none outline-none font-plus-jakarta font-normal text-[13px] leading-[18px] text-[#2d2923] dark:text-[#fdfdfd] placeholder:text-[rgba(45,41,35,0.48)] dark:placeholder:text-[rgba(253,253,253,0.48)]"
          />
        </div>
        {/* Filter Button - 54x54px */}
        <button
          className="w-[54px] h-[54px] rounded-full bg-[rgba(255,255,255,0.62)] dark:bg-[rgba(253,253,253,0.1)] border border-[rgba(45,41,35,0.08)] dark:border-[rgba(253,253,253,0.12)] flex items-center justify-center hover:bg-gray-50 dark:hover:bg-[rgba(253,253,253,0.15)] transition-colors shrink-0"
          onClick={handleFilterClick}
          aria-label="Filter options"
        >
          <SlidersHorizontal size={24} className="text-[#2d2923] dark:text-[#fdfdfd]" />
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex flex-col gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white p-4 rounded-lg animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && data.length === 0 && (
        <div className="text-center py-8 text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
          {emptyMessage}
        </div>
      )}

      {/* Table */}
      {!isLoading && data.length > 0 && (
      <div 
        ref={scrollContainerRef}
        className="overflow-x-auto scrollbar-hide -mx-1 px-1"
        {...handlers}
        style={style}
      >
        <div className="bg-[rgba(255,255,255,0.2)] inline-block min-w-full">
          {/* Header */}
          <div className="flex border-b border-[rgba(45,41,35,0.12)]">
          {columns.map((column, colIndex) => (
            <div
              key={column.key}
              className={cn(
                column.width,
                "flex items-center",
                colIndex === 0 
                  ? 'pl-[16px] pr-[8px] py-[16px]' 
                  : 'pl-0 pr-[8px] py-[8px]',
                column.sortable && 'cursor-pointer'
              )}
              onClick={() => column.sortable && onSort?.(column.key)}
            >
              {colIndex === 0 ? (
                <span className="font-plus-jakarta font-medium text-[14px] leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  {column.title}
                </span>
              ) : (
                <div 
                  className="inline-flex items-center gap-2 h-[36px] px-4 py-2 rounded-md cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation()
                    column.sortable && onSort?.(column.key)
                  }}
                >
                  <span className="font-plus-jakarta font-medium text-[14px] leading-[20px] text-[rgba(45,41,35,0.72)]">
                    {column.title}
                  </span>
                  {column.sortable && (
                    <ChevronsUpDown size={16} className="text-[#737373]" />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Rows */}
        {data.map((item, index) => (
          <div
            key={item.order_id}
            className={cn(
              "flex border-b border-[#e5e5e5] transition-colors",
              onRowClick && 'cursor-pointer hover:bg-accent',
              index % 2 === 0 && 'bg-[rgba(255,255,255,0.28)]'
            )}
            onClick={() => onRowClick?.(item)}
          >
            {columns.map((column, colIndex) => (
              <div
                key={column.key}
                className={cn(
                  column.width,
                  "flex items-center h-[68px]",
                  colIndex === 0 
                    ? 'pl-[16px] pr-[8px] py-[8px]' 
                    : 'p-[16px]'
                )}
              >
                {column.render
                  ? column.render(item)
                  : (item as any)[column.key]}
              </div>
            ))}
          </div>
        ))}
        </div>
      </div>
      )}

      {/* Filters Drawer */}
      <Drawer open={filtersOpen} onOpenChange={(open) => !open && setFiltersOpen(false)}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader className="text-center pb-4">
            <DrawerTitle className="text-[#2d2923] dark:text-[#fdfdfd] text-xl font-semibold">
              Filters & Sorting
            </DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-4 overflow-y-auto">
            <OTCFilters
              search={search || ''}
              onSearchChange={onSearchChange || (() => {})}
              type={type || ''}
              onTypeChange={onTypeChange || (() => {})}
              assetType={assetType || ''}
              onAssetTypeChange={onAssetTypeChange || (() => {})}
              trancheSize={trancheSize || ''}
              onTrancheSizeChange={onTrancheSizeChange || (() => {})}
              sortBy={sortBy || ''}
              onSortByChange={onSortByChange || (() => {})}
              sortDirection={sortDirectionFilter || ''}
              onSortDirectionChange={onSortDirectionChange || (() => {})}
              className="w-full"
              layout="grid"
            />
          </div>
          <DrawerFooter className="border-t border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] pt-4">
            <DrawerClose asChild>
              <button className="w-full bg-[rgba(45,41,35,0.72)] dark:bg-[#fdfdfd] text-white dark:text-[#2d2923] hover:bg-[#2d2923] dark:hover:bg-[rgba(253,253,253,0.9)] h-12 text-base font-plus-jakarta font-medium rounded-md transition-colors">
                Apply Filters
              </button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  )
}

